// components/individual-surveys/UpdateHistoricalNames.tsx
import React, { useMemo, useState } from "react";

type NameStat = { empname: string; cnt: number };
type StatsResponse = {
  success?: boolean;
  message?: string;
  names?: NameStat[];
  total_occurrences?: number;
  distinct_names?: number;
  affected_rows?: number;
};

export const UpdateHistoricalNames: React.FC = () => {
  const [empid, setEmpid] = useState("");
  const [empname, setEmpname] = useState("");
  const [empIdField, setEmpIdField] = useState("empid");
  const [empNameField, setEmpNameField] = useState("empname");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<NameStat[]>([]);
  const [total, setTotal] = useState(0);
  const [distinct, setDistinct] = useState(0);

  // Only need empid to fetch; need empid+empname to submit
  const canFetch = useMemo(() => empid.trim() !== "", [empid]);
  const canSubmit = useMemo(() => canFetch && empname.trim() !== "", [canFetch, empname]);

  const handleFetch = async () => {
    if (!canFetch) return;
    setLoading(true);
    setError(null);
    setStats([]);

    const params = new URLSearchParams({ empid: empid.trim() });
    if (empIdField && empIdField !== "empid") params.set("emp_id_field", empIdField.trim());
    if (empNameField && empNameField !== "empname") params.set("emp_name_field", empNameField.trim());

    try {
      const res = await fetch("/web/v1/individual-surveys/empname-stats?" + params.toString(), {
        method: "GET",
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("Fetch failed (" + res.status + ")");
      const data = (await res.json()) as StatsResponse;
      if (!data || data.success !== true) throw new Error((data && data.message) || "Unknown error");

      setStats(Array.isArray(data.names) ? data.names : []);
      setTotal(typeof data.total_occurrences === "number" ? data.total_occurrences : 0);
      setDistinct(typeof data.distinct_names === "number" ? data.distinct_names : 0);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    try {
      const form = new URLSearchParams();
      form.set("empid", empid.trim());
      form.set("empname", empname.trim());
      if (empIdField && empIdField !== "empid") form.set("emp_id_field", empIdField.trim());
      if (empNameField && empNameField !== "empname") form.set("emp_name_field", empNameField.trim());

      const res = await fetch("/web/v1/individual-surveys/update-empname", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
        },
        body: form.toString(),
      });
      if (!res.ok) throw new Error("Update failed (" + res.status + ")");
      const data = (await res.json()) as StatsResponse;
      if (!data || data.success !== true) throw new Error((data && data.message) || "Unknown error");

      await handleFetch();
      const affected = typeof data.affected_rows === "number" ? String(data.affected_rows) : "(unknown)";
      alert("Updated historical names. Rows affected: " + affected);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Update Historical Names</h2>
      </div>

      {/* Form Card */}
      <div className="rounded-2xl shadow p-4 grid gap-4 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Employee ID<span className="text-red-500">*</span>
            </label>
            <input
              className="w-full rounded-lg border px-3 py-2"
              placeholder="e.g., 623"
              value={empid}
              onChange={(e) => setEmpid(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Emp ID Field (optional)</label>
            <input
              className="w-full rounded-lg border px-3 py-2"
              placeholder="defaults to empid"
              value={empIdField}
              onChange={(e) => setEmpIdField(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Emp Name Field (optional)</label>
            <input
              className="w-full rounded-lg border px-3 py-2"
              placeholder="defaults to empname"
              value={empNameField}
              onChange={(e) => setEmpNameField(e.target.value)}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">New Employee Name (target)</label>
            <input
              className="w-full rounded-lg border px-3 py-2"
              placeholder="e.g., Breanna Yeager"
              value={empname}
              onChange={(e) => setEmpname(e.target.value)}
            />
            <p className="text-xs text-gray-500 mt-1">Leave empty if you only want to review current names.</p>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button
            onClick={handleFetch}
            disabled={!canFetch || loading}
            className="rounded-xl px-4 py-2 border shadow disabled:opacity-50"
          >
            {loading ? "Fetching..." : "Fetch current names"}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="rounded-xl px-4 py-2 border shadow disabled:opacity-50"
          >
            {submitting ? "Updating..." : "Confirm update"}
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
      </div>

      {/* Results Table */}
      <div className="rounded-2xl shadow p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">Current Names for this Employee</h3>
          <div className="text-sm text-gray-600">
            <span className="mr-4">
              Distinct: <b>{distinct}</b>
            </span>
            <span>
              Total: <b>{total}</b>
            </span>
          </div>
        </div>

        {stats.length === 0 ? (
          <p className="text-sm text-gray-500">
            No data to display. Enter parameters and click <em>Fetch current names</em>.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Count</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((row) => (
                  <tr key={String(row.empname) + "|" + String(row.cnt)} className="border-b last:border-0">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {row.empname ? row.empname : <span className="text-gray-400 italic">(empty)</span>}
                    </td>
                    <td className="py-2 pr-4">{row.cnt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export const UpdateHistoricalNamesTab: React.FC = () => {
  return (
    <div className="py-4">
      <UpdateHistoricalNames />
    </div>
  );
};
